import React from 'react';
import {
  X,
  Calendar,
  DollarSign,
  User,
  Tag,
  FileText,
  CheckCircle,
  XCircle,
  Edit2,
  Trash2,
  Clock,
  Link2,
  Building2,
  ShoppingBag,
  BookOpen,
  Image,
  ExternalLink
} from 'lucide-react';
import { useNavigate } from 'react-router-dom';

const ExpenseDetailModal = ({
  expense,
  isOpen,
  onClose,
  onEdit,
  onApprove,
  onReject,
  onDelete,
  onPostToGL,
  isAdmin,
  user
}) => {
  const navigate = useNavigate();

  if (!isOpen || !expense) return null;

  const getStatusBadge = (status) => {
    const statusConfig = {
      draft: { color: 'bg-gray-100 text-gray-800', icon: FileText },
      pending: { color: 'bg-yellow-100 text-yellow-800', icon: Clock },
      approved: { color: 'bg-green-100 text-green-800', icon: CheckCircle },
      rejected: { color: 'bg-red-100 text-red-800', icon: XCircle },
      posted: { color: 'bg-blue-100 text-blue-800', icon: CheckCircle }
    };

    const config = statusConfig[status] || statusConfig.pending;
    const Icon = config.icon;

    return (
      <span className={`inline-flex items-center gap-1 px-3 py-1 rounded-full text-sm font-medium ${config.color}`}>
        <Icon size={14} />
        {status.charAt(0).toUpperCase() + status.slice(1)}
      </span>
    );
  };

  const formatDate = (dateString) => {
    return new Date(dateString).toLocaleDateString('en-NG', {
      weekday: 'long',
      year: 'numeric',
      month: 'long',
      day: 'numeric'
    });
  };

  const formatCurrency = (amount) => {
    return new Intl.NumberFormat('en-NG', {
      style: 'currency',
      currency: 'NGN'
    }).format(amount);
  };

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4" onClick={(e) => {
        if (e.target === e.currentTarget) onClose();
    }}>
      <div className="bg-white rounded-lg shadow-xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        {/* Header */}
        <div className="px-6 py-4 border-b border-gray-200 flex justify-between items-center sticky top-0 bg-white z-10">
          <div>
            <h2 className="text-xl font-bold text-gray-900">Expense Details</h2>
            <p className="text-sm text-gray-500">{expense.expense_number}</p>
          </div>
          <div className="flex items-center gap-2">

            <button
              onClick={onClose}
              className="text-gray-400 hover:text-gray-600 p-2 rounded-full hover:bg-gray-100 transition-colors"
            >
              <X size={20} />
            </button>
          </div>
        </div>

        {/* Content */}
        <div className="p-6 space-y-6">
          {/* Status Bar */}
          <div className="flex justify-between items-center bg-gray-50 p-4 rounded-lg">
            <span className="text-sm font-medium text-gray-700">Current Status</span>
            {getStatusBadge(expense.status)}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            {/* Primary Info */}
            <div className="space-y-4">
              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Amount
                </label>
                <div className="flex items-center text-2xl font-bold text-gray-900">
                  <DollarSign className="w-6 h-6 text-gray-400 mr-1" />
                  {formatCurrency(expense.amount)}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Category
                </label>
                <div className="flex items-center text-gray-900">
                  <Tag className="w-4 h-4 text-gray-400 mr-2" />
                  {expense.category_name}
                </div>
              </div>

              <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Date
                </label>
                <div className="flex items-center text-gray-900">
                  <Calendar className="w-4 h-4 text-gray-400 mr-2" />
                  {formatDate(expense.expense_date)}
                </div>
              </div>
            </div>

            {/* Secondary Info */}
            <div className="space-y-4">
               <div>
                <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-1">
                  Submitted By
                </label>
                <div className="flex items-center text-gray-900">
                  <User className="w-4 h-4 text-gray-400 mr-2" />
                  {expense.submitted_by_name}
                </div>
              </div>

              {/* Linked Order Section */}
              {expense.wholesale_order_id && (
                <div className="bg-blue-50 p-3 rounded-lg border border-blue-100">
                   <h4 className="text-xs font-bold text-blue-700 uppercase tracking-wider mb-2 flex items-center gap-1">
                      <Link2 size={12} />
                      Linked Order
                   </h4>
                   
                   <div className="space-y-2">
                       <div className="flex items-center justify-between">
                            <span className="text-sm text-blue-600 font-medium">Order Reference:</span>
                             <button
                                onClick={() => navigate(`/wholesale/orders/${expense.wholesale_order_id}`)}
                                className="inline-flex items-center gap-1 text-sm font-bold text-blue-700 hover:text-blue-900 hover:underline"
                            >
                                {expense.order_number || `Order #${expense.wholesale_order_id}`}
                                <Link2 size={12} />
                            </button>
                       </div>

                       {/* Customer Name linked to order */}
                        {expense.customer_name && (
                            <div className="flex items-center justify-between">
                                <span className="text-sm text-blue-600 font-medium">Customer:</span>
                                <span className="text-sm font-bold text-blue-700">{expense.customer_name}</span>
                            </div>
                        )}
                   </div>
                </div>
              )}
            </div>
          </div>

          {/* Description */}
          <div>
            <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2">
              Description
            </label>
            <div className="bg-gray-50 p-4 rounded-lg text-gray-700 whitespace-pre-wrap">
              {expense.description || 'No description provided.'}
            </div>
          </div>

          {/* Receipt/Invoice Section */}
          {expense.receipt_url && (
            <div>
              <label className="text-xs font-semibold text-gray-500 uppercase tracking-wider block mb-2 flex items-center gap-1">
                <Image size={14} />
                Receipt/Invoice
              </label>
              <div className="bg-gray-50 p-4 rounded-lg border border-gray-200">
                {expense.receipt_url.startsWith('data:image') || 
                 expense.receipt_url.startsWith('blob:') || 
                 expense.receipt_url.match(/\.(jpg|jpeg|png|gif|webp)$/i) ? (
                  <div className="space-y-2">
                    <img
                      src={expense.receipt_url}
                      alt="Receipt"
                      className="max-w-full max-h-64 rounded-lg object-contain mx-auto cursor-pointer hover:opacity-90 transition-opacity"
                      onClick={() => window.open(expense.receipt_url, '_blank')}
                    />
                    <p className="text-xs text-gray-500 text-center">Click to view full size</p>
                  </div>
                ) : expense.receipt_url.match(/\.pdf$/i) ? (
                  <a
                    href={expense.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <FileText size={20} />
                    View PDF Receipt
                    <ExternalLink size={14} />
                  </a>
                ) : (
                  <a
                    href={expense.receipt_url}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="flex items-center gap-2 text-blue-600 hover:text-blue-800 font-medium"
                  >
                    <FileText size={20} />
                    View Attached Receipt
                    <ExternalLink size={14} />
                  </a>
                )}
              </div>
            </div>
          )}

          {/* Rejection Reason (if rejected) */}
          {expense.status === 'rejected' && expense.rejection_reason && (
             <div className="bg-red-50 p-4 rounded-lg border border-red-100">
                <label className="text-xs font-semibold text-red-700 uppercase tracking-wider block mb-2">
                  Rejection Reason
                </label>
                <p className="text-red-800">{expense.rejection_reason}</p>
             </div>
          )}
        </div>

        {/* Footer Actions */}
        <div className="px-6 py-4 bg-gray-50 border-t border-gray-200 flex flex-wrap justify-end gap-2 sm:gap-3 rounded-b-lg">
          {/* Delete Action - Left aligned */}
          {(isAdmin || (
            (expense.status === 'draft' || expense.status === 'pending' || expense.status === 'rejected') &&
            expense.submitted_by === user?.id
          )) && (
            <button
              onClick={() => onDelete(expense.id, expense.expense_number)}
              className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors text-sm sm:text-base mr-auto"
              title={isAdmin ? "Delete expense (will reverse GL if posted)" : "Delete expense"}
            >
              <Trash2 size={16} />
              Delete
            </button>
          )}
          {/* Edit for draft/pending and own expenses */}
          {(expense.status === 'draft' || expense.status === 'pending') &&
            (isAdmin || expense.submitted_by === user?.id) && (
            <button
              onClick={() => {
                onClose();
                onEdit(expense);
              }}
              className="flex items-center gap-2 px-4 py-2 bg-white border border-gray-300 rounded-lg text-gray-700 hover:bg-gray-50 font-medium transition-colors text-sm sm:text-base"
            >
              <Edit2 size={16} />
              Edit
            </button>
          )}

          {/* Admin Approval Actions */}
          {isAdmin && expense.status === 'pending' && (
            <>
              <button
                onClick={() => onReject(expense)}
                className="flex items-center gap-2 px-4 py-2 bg-red-100 text-red-700 rounded-lg hover:bg-red-200 font-medium transition-colors text-sm sm:text-base"
              >
                <XCircle size={16} />
                Reject
              </button>
              <button
                onClick={() => onApprove(expense.id)}
                className="flex items-center gap-2 px-4 py-2 bg-green-600 text-white rounded-lg hover:bg-green-700 font-medium transition-colors text-sm sm:text-base"
              >
                <CheckCircle size={16} />
                Approve
              </button>
            </>
          )}

          {/* Post to GL Action */}
          {isAdmin && expense.status === 'approved' && (
            <button
              onClick={() => onPostToGL(expense.id)}
              className="flex items-center gap-2 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 font-medium transition-colors text-sm sm:text-base"
            >
              <BookOpen size={16} />
              Post to GL
            </button>
          )}

          <button
             onClick={onClose}
             className="px-4 py-2 text-gray-600 hover:text-gray-800 font-medium text-sm sm:text-base"
          >
              Close
          </button>
        </div>
      </div>
    </div>
  );
};

export default ExpenseDetailModal;
